import { type ClerkProvider } from '@clerk/clerk-react';
import { deDE, enUS } from '@clerk/localizations';

type ClerkLocalization = NonNullable<
  React.ComponentProps<typeof ClerkProvider>['localization']
>;

type PortalLanguage = 'de' | 'en';

interface ClerkStartStep {
  title?: string;
  subtitle?: string;
}

interface ClerkOverrides {
  signIn?: { start?: ClerkStartStep };
  signUp?: { start?: ClerkStartStep };
}

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

function deepMerge<T extends Record<string, unknown>>(target: T, source: DeepPartial<T>): T {
  const result: T = { ...target };
  for (const [key, value] of Object.entries(source)) {
    const existing: unknown = result[key];
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      existing &&
      typeof existing === 'object'
    ) {
      (result as Record<string, unknown>)[key] = deepMerge(
        existing as Record<string, unknown>,
        value as DeepPartial<Record<string, unknown>>,
      );
    } else {
      (result as Record<string, unknown>)[key] = value;
    }
  }
  return result;
}

const clerkCustomizations: Record<PortalLanguage, ClerkOverrides> = {
  de: {
    signIn: {
      start: {
        title: 'Willkommen bei cargovibe',
        subtitle: 'Melde dich an, um das Customer Admin Portal zu nutzen.',
      },
    },
    signUp: {
      start: {
        title: 'Konto erstellen',
        subtitle: 'Registriere dich, um das Customer Admin Portal zu nutzen.',
      },
    },
  },
  en: {
    signIn: {
      start: {
        title: 'Welcome to cargovibe',
        subtitle: 'Sign in to use the Customer Admin Portal.',
      },
    },
    signUp: {
      start: {
        title: 'Create your account',
        subtitle: 'Sign up to use the Customer Admin Portal.',
      },
    },
  },
};

const baseLocales: Record<PortalLanguage, ClerkLocalization> = {
  de: deDE,
  en: enUS,
};

const clerkLocalizationMap: Record<PortalLanguage, ClerkLocalization> = {
  de: deepMerge(baseLocales.de, clerkCustomizations.de),
  en: deepMerge(baseLocales.en, clerkCustomizations.en),
};

export function resolveClerkLocalization(language: string): ClerkLocalization {
  const prefix: string = language.split('-')[0].toLowerCase();
  return prefix === 'en' ? clerkLocalizationMap.en : clerkLocalizationMap.de;
}
